<template>
  <div class="reverse-ip-lookup-app" data-testid="reverse-ip-app">
    <a-card title="Reverse IP Lookup" class="main-card" data-testid="reverse-ip-card">
      <template #extra>
        <export-button
          v-if="result"
          :results="result.domains"
          @export="handleExport"
          data-testid="reverse-ip-export-button"
        />
      </template>

      <ip-input :loading="loading" @lookup="handleLookup" data-testid="reverse-ip-input-component" />

      <a-alert
        v-if="error"
        type="error"
        :message="error"
        closable
        show-icon
        class="error-alert"
        @close="error = null"
        data-testid="reverse-ip-error"
      />

      <div ref="resultsSection">
        <results-table
          v-if="result"
          :domains="result.domains"
          :ip-address="result.ip_address"
          :query-time="result.query_time"
          :metadata="result.metadata"
          :loading="loading"
          data-testid="reverse-ip-results-table"
        />

        <a-empty
          v-else-if="!loading && !error"
          description="Enter an IP address to discover domains"
          class="empty-state"
          data-testid="reverse-ip-empty-state"
        >
          <template #image>
            <SearchOutlined style="font-size: 64px; color: #bfbfbf" />
          </template>
        </a-empty>
      </div>
    </a-card>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import { SearchOutlined } from '@ant-design/icons-vue';
import IpInput from './components/IpInput.vue';
import ResultsTable from './components/ResultsTable.vue';
import ExportButton from './components/ExportButton.vue';
import { useReverseIpLookup } from './composables/useReverseIpLookup';

const { loading, error, result, lookup, exportCSV, exportJSON } = useReverseIpLookup();

const resultsSection = ref<HTMLElement | null>(null);

watch(result, (newResult) => {
  if (newResult) {
    nextTick(() => {
      resultsSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }
});

const handleLookup = async (ipAddress: string) => {
  await lookup(ipAddress);
};

const handleExport = (format: 'csv' | 'json') => {
  if (!result.value?.domains) {
    return;
  }

  if (format === 'csv') {
    exportCSV(result.value.domains);
  } else {
    exportJSON(result.value.domains);
  }
};
</script>

<style scoped lang="scss">
.reverse-ip-lookup-app {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.main-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  :deep(.ant-card-head) {
    font-weight: 700;
    font-size: 20px;
  }

  :deep(.ant-card-head-title) {
    color: white;
  }

  :deep(.ant-card-body) {
    padding: 24px;
  }
}

.error-alert {
  margin-bottom: 24px;
}

.empty-state {
  padding: 60px 0;
}
</style>
